/**
 * project-economics.js — Per-project economic assumption defaults.
 *
 * V1: localStorage keyed by projectId. Autofills LG / NPV / Cutoff Grade dialogs
 * so an engineer doesn't retype the same G&A, recovery, royalty every session.
 *
 * Can be upgraded to a proper backend collection (Project.economic_defaults) in
 * a later sprint without changing the caller API.
 */

const KEY_PREFIX = "smin_econ_v1_";

/**
 * Canonical shape. Extend as new engineering fields are added.
 * Monetary values are stored in the PROJECT currency (see MinePlanTab.sym).
 * Percentages are stored as 0..100 (not 0..1) for user-friendly UI.
 */
export const DEFAULT_ECONOMICS = {
  // Revenue / metallurgy
  revenue_per_grade_unit: 60,   // $/t · %grade (or /t · g/t depending on units)
  recovery_pct: 90,             // Mill/plant recovery %
  payable_pct: 97,              // Smelter payable share %
  // Costs (per tonne of ore or waste as noted)
  mining_cost: 3.5,             // $/t mined
  processing_cost: 12,          // $/t milled
  ga_cost: 1.5,                 // General & Admin overhead /t milled
  selling_cost: 4,              // Treatment + refining + freight /t conc
  // Fiscal
  royalty_pct: 3,               // Government / land-owner royalty on NSR
  // Optimisation
  slope_deg: 45,                // Pit slope
  discount_rate: 0.08,          // NPV discount rate (fraction)
  // Per-field input currencies (ISO codes) — enables multi-currency entry
  input_currencies: {
    revenue_per_grade_unit: null, // null → same as project currency
    mining_cost: null,
    processing_cost: null,
    ga_cost: null,
    selling_cost: null,
  },
};

export function loadEconomics(projectId) {
  if (!projectId) return { ...DEFAULT_ECONOMICS };
  try {
    const raw = localStorage.getItem(KEY_PREFIX + projectId);
    if (!raw) return { ...DEFAULT_ECONOMICS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_ECONOMICS, ...parsed,
      input_currencies: { ...DEFAULT_ECONOMICS.input_currencies, ...(parsed.input_currencies || {}) },
    };
  } catch (err) {
    console.warn("[project-economics] load failed:", err?.message);
    return { ...DEFAULT_ECONOMICS };
  }
}

export function saveEconomics(projectId, econ) {
  if (!projectId) return;
  try {
    localStorage.setItem(KEY_PREFIX + projectId, JSON.stringify(econ));
  } catch (err) {
    console.warn("[project-economics] save failed:", err?.message);
  }
}

/**
 * Mining-engineering-standard NSR cutoff grade formula.
 * Returns a percent (not fraction) if grade unit is %, or g/t if grade unit is g/t.
 *
 *   cutoff = (mining + processing + G&A) /
 *            (price × recovery × payable × (1 − royalty))
 */
export function calcCutoffGrade(econ) {
  const cost = (econ.mining_cost || 0) + (econ.processing_cost || 0) + (econ.ga_cost || 0);
  const rec = (econ.recovery_pct || 0) / 100;
  const pay = (econ.payable_pct || 0) / 100;
  const roy = 1 - (econ.royalty_pct || 0) / 100;
  const price = econ.revenue_per_grade_unit || 0;
  const denom = price * rec * pay * roy;
  if (!Number.isFinite(denom) || denom <= 0) return null;
  return cost / denom;
}
